import React, { useState, useRef, useEffect } from 'react';
import { Upload, Image as ImageIcon, X, Eye, Trash2, RefreshCw, AlertCircle, Check } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { API_BASE_URL, apiClient } from '../../services/api';
import { toAbs } from '../../utils/url';

interface HeroImageManagerProps {
  onImageChange?: (imageUrl: string) => void;
  onCustomCssChange?: (value: string) => void;
}

const cardStyle: React.CSSProperties = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' };
const labelStyle: React.CSSProperties = { color: '#9ca3af' };

const HeroImageManager: React.FC<HeroImageManagerProps> = ({ onImageChange, onCustomCssChange }) => {
  const { token } = useAuth();
  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [fadeHex, setFadeHex] = useState<string>('#000000');
  const [fadeAlpha, setFadeAlpha] = useState<number>(0.8);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const hexToRgb = (hex: string) => {
    const s = hex.replace('#', '');
    return { r: parseInt(s.slice(0,2),16), g: parseInt(s.slice(2,4),16), b: parseInt(s.slice(4,6),16) };
  };

  useEffect(() => {
    loadCurrentImage();
    try {
      const varValue = getComputedStyle(document.documentElement).getPropertyValue('--hero-bottom-fade-to').trim();
      if (varValue) {
        const m = varValue.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*(0?\.\d+|1(?:\.0+)?))?\)/i);
        if (m) {
          const toHex = (n: number) => parseInt(m[n],10).toString(16).padStart(2,'0');
          setFadeHex(`#${toHex(1)}${toHex(2)}${toHex(3)}`);
          setFadeAlpha(m[4] ? parseFloat(m[4]) : 1);
        }
      }
    } catch (_) {}
  }, []);

  const loadCurrentImage = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/hero/background`);
      const data = await response.json();
      if (data.url) {
        const abs = toAbs(data.url);
        setCurrentImage(abs);
        onImageChange?.(abs);
      }
    } catch (error) {
      console.error('Erro ao carregar imagem atual:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) uploadCustomImage(file);
  };

  const uploadCustomImage = async (file: File) => {
    if (!token) { setMessage({ type: 'error', text: 'Token de autenticação não encontrado' }); return; }
    setUploading(true); setUploadProgress(0); setMessage(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiClient.post(`/hero/upload-background`, formData, {
        headers: { 'Authorization': `Bearer ${token}` },
        onUploadProgress: (e) => { if (e.total) setUploadProgress(Math.round((e.loaded/e.total)*100)); },
      });
      const abs = toAbs(res.data?.url || '');
      if (abs) { setCurrentImage(abs); onImageChange?.(abs); }
      setMessage({ type: 'success', text: 'Imagem enviada com sucesso!' });
    } catch (error) {
      const status = (error as any)?.response?.status;
      const raw = (error as any)?.response?.data?.detail;
      const detail = status === 401 ? 'Sessão expirada. Faça login novamente.'
        : Array.isArray(raw) ? raw.map((r: any) => r?.msg ?? JSON.stringify(r)).join('; ')
        : typeof raw === 'string' ? raw : 'Erro ao enviar imagem';
      setMessage({ type: 'error', text: detail });
    } finally {
      setUploading(false); setUploadProgress(0);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const removeCurrentImage = async () => {
    if (!token) { setMessage({ type: 'error', text: 'Token de autenticação não encontrado' }); return; }
    try {
      const response = await fetch(`${API_BASE_URL}/hero/background`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setCurrentImage(null);
        onImageChange?.('');
        setMessage({ type: 'success', text: data.message });
      } else {
        throw new Error(data.detail || 'Erro ao remover imagem');
      }
    } catch {
      setMessage({ type: 'error', text: 'Erro ao remover imagem' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-5 h-5 animate-spin" style={{ color: '#6b7280' }} />
        <span className="ml-2 text-sm" style={{ color: '#6b7280' }}>Carregando...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toast */}
      {message && (
        <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-500/15 text-green-300' : 'bg-red-500/15 text-red-300'}`}
          style={{ border: `1px solid ${message.type === 'success' ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}` }}>
          {message.type === 'success' ? <Check className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
          {message.text}
        </div>
      )}

      {/* Imagem atual */}
      <div className="rounded-xl p-4" style={cardStyle}>
        <div className="text-sm font-medium text-white mb-3 flex items-center gap-2">
          <ImageIcon className="w-4 h-4" style={{ color: '#3b82f6' }} />
          Imagem de Fundo Atual
        </div>
        {currentImage ? (
          <div className="space-y-3">
            <div className="relative group">
              <img src={currentImage} alt="Fundo atual" className="w-full h-48 object-cover rounded-lg" />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                <button onClick={() => setPreviewImage(currentImage)} className="p-2 rounded-lg bg-white/20 hover:bg-white/30">
                  <Eye className="w-4 h-4 text-white" />
                </button>
                <button onClick={removeCurrentImage} className="p-2 rounded-lg bg-red-500/30 hover:bg-red-500/50">
                  <Trash2 className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <ImageIcon className="w-10 h-10 mx-auto mb-2" style={{ color: '#4b5563' }} />
            <p className="text-sm" style={labelStyle}>Nenhuma imagem de fundo configurada</p>
          </div>
        )}
      </div>

      {/* Upload */}
      <div className="rounded-xl p-4" style={cardStyle}>
        <div className="text-sm font-medium text-white mb-3 flex items-center gap-2">
          <Upload className="w-4 h-4" style={{ color: '#3b82f6' }} />
          Enviar Imagem Personalizada
        </div>
        <div className="text-xs mb-4 space-y-0.5" style={labelStyle}>
          <p>Formatos aceitos: JPEG, PNG, WebP, SVG</p>
          <p>Tamanho máximo: 5 MB · Recomendado: 1920×1080 px</p>
        </div>
        {uploading ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm" style={labelStyle}>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Enviando... {uploadProgress}%
            </div>
            <div className="w-full rounded-full h-1.5" style={{ background: 'rgba(255,255,255,0.08)' }}>
              <div className="h-1.5 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%`, background: '#2563eb' }} />
            </div>
          </div>
        ) : (
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full flex items-center justify-center gap-2 py-2 rounded-md text-white text-sm font-medium"
            style={{ background: '#2563eb' }}
          >
            <Upload className="w-3.5 h-3.5" />
            Selecionar Arquivo
          </button>
        )}
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
      </div>

      {/* Gradiente do Hero */}
      <div className="rounded-xl p-4" style={cardStyle}>
        <div className="text-sm font-medium text-white mb-1 flex items-center gap-2">
          <ImageIcon className="w-4 h-4" style={{ color: '#3b82f6' }} />
          Gradiente do Hero (cor inferior)
        </div>
        <p className="text-xs mb-4" style={labelStyle}>
          Controla a variável CSS <code className="font-mono">--hero-bottom-fade-to</code> usada no rodapé do Hero.
        </p>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={fadeHex}
              onChange={(e) => {
                const newHex = e.target.value;
                setFadeHex(newHex);
                const { r, g, b } = hexToRgb(newHex);
                const rgba = `rgba(${r}, ${g}, ${b}, ${fadeAlpha})`;
                document.documentElement.style.setProperty('--hero-bottom-fade-to', rgba);
                onCustomCssChange?.(rgba);
              }}
              className="w-10 h-10 rounded-lg cursor-pointer"
              style={{ border: '1px solid rgba(255,255,255,0.1)', background: 'transparent' }}
            />
            <input
              type="text"
              value={fadeHex}
              onChange={(e) => {
                const val = e.target.value;
                if (/^#([0-9a-fA-F]{6})$/.test(val)) {
                  setFadeHex(val);
                  const { r, g, b } = hexToRgb(val);
                  const rgba = `rgba(${r}, ${g}, ${b}, ${fadeAlpha})`;
                  document.documentElement.style.setProperty('--hero-bottom-fade-to', rgba);
                  onCustomCssChange?.(rgba);
                } else {
                  setFadeHex(val);
                }
              }}
              className="px-3 py-2 rounded-md text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
            />
          </div>
          <div className="flex items-center gap-2 flex-1">
            <label className="text-xs flex-shrink-0" style={labelStyle}>Opacidade</label>
            <input
              type="range" min={0} max={1} step={0.01} value={fadeAlpha}
              onChange={(e) => {
                const newAlpha = parseFloat(e.target.value);
                setFadeAlpha(newAlpha);
                const { r, g, b } = hexToRgb(fadeHex);
                const rgba = `rgba(${r}, ${g}, ${b}, ${newAlpha})`;
                document.documentElement.style.setProperty('--hero-bottom-fade-to', rgba);
                onCustomCssChange?.(rgba);
              }}
              className="flex-1"
            />
            <span className="text-xs w-10 text-right" style={labelStyle}>{fadeAlpha.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Modal de preview */}
      {previewImage && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setPreviewImage(null)}>
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <img src={previewImage} alt="Preview" className="max-w-full max-h-[80vh] object-contain rounded-xl" />
            <button onClick={() => setPreviewImage(null)} className="absolute -top-3 -right-3 p-1.5 rounded-full bg-black/60 hover:bg-black/80">
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeroImageManager;
